import "./style.scss";

import { Skeleton, Tooltip } from "@mui/material";
import ActionArrowLeft from "assets/img/svg/actionArrowLeft";
import { getAgreementRelatedResourcePath } from "components/AgreementValidationModal/utils";
import StyledButton from "components/shared/StyledButton";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { Body, PanelTitle, TinyBody } from "helpers/TypographyComponents";
import { formatLocaleDateString, getJsonOrFalse } from "helpers/utils";
import moment from "moment/min/moment-with-locales";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { AgreementTemplate } from "store/agreementsStore";
import { Agreement, AgreementSignatureStatus, AgreementStatus } from "store/agreementsStore";
import type { IArticle } from "store/articleStore";
import type { ITerm } from "store/managementStore";
import type { TFetchStatus, TPortalType } from "store/types";
import { EPortalType } from "store/types";
import type { IUser } from "types";

import { useAgreementPageContext } from "../context/StateProvider";

interface IContractValidationHeadPanel {
    /**
     * Return to agreements function
     */
    handleReturnToAgreements: () => void;
    /**
     * Contract validation creator
     */
    owner: IUser | undefined;
    /**
     * Related fetct term or article status
     */
    relatedResourceStatus: TFetchStatus;
    /**
     * Related term or article data
     */
    relatedResourceData: ITerm | IArticle;
    agreementTemplate: AgreementTemplate | null;
    /**
     * Save agreement changes function
     */
    handleSaveAgreement: () => void;
    /**
     * Options for ThreeDotMenuOptions component
     */
    ThreeDotMenuOptions: ThreeDotMenuOptions[];
    /**
     * If saving changes is pending, to render loading saving changes button
     */
    isSaving: boolean;
    /**
     * Portal type
     */
    portalType: TPortalType;
}

const ContractValidationHeadPanel = ({
    handleReturnToAgreements,
    owner,
    relatedResourceStatus,
    relatedResourceData,
    agreementTemplate,
    handleSaveAgreement,
    ThreeDotMenuOptions,
    isSaving,
    portalType,
}: IContractValidationHeadPanel) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const { agreement, isDraft, isOwner, isSigned } = useAgreementPageContext();

    const lang = getJsonOrFalse(localStorage.getItem("settings") ?? "{}").accountSettings?.language || "fr";
    const signedAt = formatLocaleDateString({
        date: agreement?.lastUpdate ?? "",
        options: { numeric: true, timeZone: "UTC" },
    });
    const editedAt = moment(agreement?.lastUpdate)
        .locale(lang)
        .fromNow();
    const isSentToSign = agreement?.signedStatus === AgreementSignatureStatus.SENT;

    const relatedResourcePath = getAgreementRelatedResourcePath(portalType, {
        workId: portalType === EPortalType.BOOK ? (relatedResourceData as ITerm).workId : null,
        titleId: relatedResourceData?.titleId,
    });

    return (
        <>
            <div className="contract-validation-edit-left-panel-top">
                <StyledButton buttonType="secondary" onClick={handleReturnToAgreements}>
                    <ActionArrowLeft />
                    {t("ContractValidationEdit.BackToContracts")}
                </StyledButton>
                {relatedResourceStatus === "succeeded" ? (
                    <div className="contract-validation-edit-issued-on">
                        <TinyBody color="grey">
                            {t("ContractValidationEdit.CreatedBy")} {owner?.firstname ?? ""} {owner?.lastname ?? ""}
                        </TinyBody>
                        <Tooltip
                            title={
                                !isSigned
                                    ? formatLocaleDateString({
                                          date: agreement?.lastUpdate ?? "",
                                          options: { numeric: true, timeZone: "UTC" },
                                      })
                                    : ""
                            }
                        >
                            <div>
                                <TinyBody color="grey">
                                    {isDraft && t("ContractValidationEdit.LastModified", { editedAt })}
                                    {isSentToSign && t("ContractValidationEdit.Sent")}
                                    {isSigned && t("ContractValidationEdit.SignedOn", { signedAt })}
                                </TinyBody>
                            </div>
                        </Tooltip>
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                        <Skeleton width={"10rem"} />
                        <Skeleton width={"14rem"} />
                    </div>
                )}
            </div>
            <div className="contract-validation-edit-left-panel-header">
                <div>
                    {relatedResourceStatus !== "succeeded" ? (
                        <Skeleton width={"20rem"} />
                    ) : (
                        <>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <PanelTitle>
                                    {t(
                                        agreement?.type === "avenant"
                                            ? "ContractSettings.TypeAvenant"
                                            : agreement?.relatedResource === "frameworkContract"
                                              ? "ContractValidation.FrameworkContract"
                                              : "ContractValidationEdit.Contract",
                                    )}{" "}
                                    {relatedResourcePath ? (
                                        <span
                                            title={t("ContractValidationEdit.NavigateToContract")}
                                            style={{ fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}
                                            onClick={() =>
                                                navigate(
                                                    relatedResourcePath,
                                                    portalType === EPortalType.BOOK
                                                        ? { state: { filterList: true } }
                                                        : undefined,
                                                )
                                            }
                                        >
                                            {relatedResourceData?.title}{" "}
                                        </span>
                                    ) : (
                                        <span style={{ fontWeight: 600 }}>{relatedResourceData?.title} </span>
                                    )}
                                    {t("ContractValidationEdit.For")}{" "}
                                    <span
                                        title={t("ContractValidationEdit.NavigateToBeneficiary")}
                                        style={{ fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}
                                        onClick={() => navigate(`/beneficiars/${relatedResourceData?.beneficiarId}`)}
                                    >
                                        {relatedResourceData?.author}
                                    </span>
                                </PanelTitle>
                                {agreement?.status && (
                                    <span
                                        className={`agreement-type-badge ${
                                            agreement.signedStatus === AgreementSignatureStatus.REFUSED
                                                ? "status-refused"
                                                : agreement.signedStatus === AgreementSignatureStatus.SIGNED
                                                  ? "status-signed"
                                                  : `status-${agreement.status}`
                                        }`}
                                    >
                                        {agreement.signedStatus === AgreementSignatureStatus.REFUSED
                                            ? t("ContractValidation.StatusRefused")
                                            : agreement.signedStatus === AgreementSignatureStatus.SIGNED
                                              ? t("ContractValidation.StatusSigned")
                                              : t(`ContractValidation.Status${agreement.status}`)}
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                    {relatedResourceStatus !== "succeeded" ? (
                        <Skeleton width={"22rem"} />
                    ) : (
                        <>
                            {agreementTemplate ? (
                                <Body color="grey">
                                    {t("ContractValidationEdit.BasedOn")}{" "}
                                    <span
                                        title={t("ContractValidationEdit.NavigateToTemplate")}
                                        style={{ textDecoration: "underline", cursor: "pointer" }}
                                        onClick={() =>
                                            navigate(`/settings/contracts`, {
                                                state: { templateId: agreementTemplate?.id },
                                            })
                                        }
                                    >
                                        {" "}
                                        {agreementTemplate?.name}
                                    </span>
                                </Body>
                            ) : (
                                <Tooltip title={t("ContractValidationEdit.TemplateDeleted")}>
                                    <div className="contract-validation-edit-template-info">
                                        <Body color="grey">{t("ContractValidationEdit.TemplateUnavailable")}</Body>
                                    </div>
                                </Tooltip>
                            )}
                        </>
                    )}
                </div>
                <div style={{ display: "flex", gap: "4px" }}>
                    {isDraft && isOwner && relatedResourceStatus === "succeeded" && (
                        <StyledButton
                            buttonType="primary"
                            onClick={handleSaveAgreement}
                            disabled={isSaving}
                            loading={isSaving}
                        >
                            <span style={isSaving ? { opacity: 0 } : {}}>{t("ContractValidationEdit.Save")}</span>
                        </StyledButton>
                    )}
                    <ThreeDotMenu options={ThreeDotMenuOptions} style={{ borderColor: "black" }} />
                </div>
            </div>
        </>
    );
};

export default ContractValidationHeadPanel;
